import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Shield, UserCheck, UserX, Users as UsersIcon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPhoneNumber, getPhoneValidationError } from '../utils/validation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Users = () => {
  const { token, hasPermission } = useAuth();
  const { t } = useApp();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'cs',
    branch_id: '',
    phone: ''
  });
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (hasPermission(['admin', 'super_admin', 'kepala_cabang']) && token) {
      fetchUsers();
      fetchBranches();
      fetchRoles();
    }
  }, [token]);

  useEffect(() => {
    const filtered = users.filter(user =>
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUsers(response.data.data);
        setFilteredUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchBranches = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRoles(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      // Fallback to default roles if API fails
      setRoles([
        { name: 'admin', description: 'Administrator' },
        { name: 'kepala_cabang', description: 'Kepala Cabang' },
        { name: 'cs', description: 'Customer Service' },
        { name: 'teknisi', description: 'Teknisi' }
      ]);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone if provided
    if (formData.phone) {
      const error = getPhoneValidationError(formData.phone);
      if (error) {
        setPhoneError(error);
        toast.error('Nomor telepon tidak valid');
        return;
      }
    }
    
    try {
      if (isEditMode) {
        // Update existing user
        const updateData = { ...formData };
        // Don't send password if it's empty during edit
        if (!updateData.password) {
          delete updateData.password;
        }
        await axios.put(`${API_URL}/api/users/${editingUserId}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User updated successfully');
      } else {
        // Create new user - use /api/users with token for tenant isolation
        await axios.post(`${API_URL}/api/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User created successfully');
      }
      setIsDialogOpen(false);
      setIsEditMode(false);
      setEditingUserId(null);
      fetchUsers();
      setFormData({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role: 'cs',
        branch_id: '',
        phone: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} user`);
    }
  };

  const handleEdit = (user) => {
    console.log('handleEdit called with user:', user);
    setIsEditMode(true);
    setEditingUserId(user.id);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      password: '', // Leave empty, will be optional on update
      role: user.role,
      branch_id: user.branch_id || '',
      phone: user.phone || ''
    });
    console.log('Opening dialog with edit mode');
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API_URL}/api/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const getRoleLabel = (roleName) => {
    // Try to find role in fetched roles
    const roleObj = roles.find(r => r.name === roleName);
    if (roleObj && roleObj.description) {
      return roleObj.description;
    }
    
    // Fallback to default labels
    const defaultRoles = {
      admin: 'Administrator',
      kepala_cabang: 'Kepala Cabang',
      cs: 'Customer Service',
      teknisi: 'Teknisi'
    };
    return defaultRoles[roleName] || roleName;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-500 hover:bg-red-600',
      kepala_cabang: 'bg-blue-500 hover:bg-blue-600',
      cs: 'bg-emerald-500 hover:bg-emerald-600',
      teknisi: 'bg-amber-500 hover:bg-amber-600'
    };
    return colors[role] || 'bg-purple-500 hover:bg-purple-600'; // Purple for custom roles
  };

  // Debug permission check
  console.log('Users page - hasPermission check:', {
    hasPermission: hasPermission(['admin', 'super_admin']),
    token: !!token,
    users: users.length
  });

  if (!hasPermission(['admin', 'super_admin', 'kepala_cabang'])) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-slate-900">
        <Card className="border-0 shadow-sm dark:bg-slate-800 max-w-md">
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-sm text-slate-500 dark:text-slate-400">You don't have permission to view this page</p>
            <p className="text-xs text-slate-400 mt-2 dark:text-slate-500">Required role: Admin or Super Admin</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6 dark:bg-slate-800 dark:border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg dark:bg-indigo-900/40">
                <UsersIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">User Management</h1>
                <p className="text-sm text-gray-500 mt-0.5 dark:text-slate-400">Manage system users and permissions</p>
              </div>
            </div>
            
            {/* Add User Button */}
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              console.log('Dialog onOpenChange:', open);
              if (!open) {
                setIsEditMode(false);
                setEditingUserId(null);
              }
              setIsDialogOpen(open);
            }}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="add-user-btn" 
                  className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-5"
                  onClick={() => {
                    setIsEditMode(false);
                    setEditingUserId(null);
                    setFormData({
                      username: '',
                      email: '',
                      full_name: '',
                      password: '',
                      role: 'cs',
                      branch_id: '',
                      phone: ''
                    });
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl dark:bg-slate-800">
                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold dark:text-white">{isEditMode ? 'Edit User' : 'Add New User'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                  <div className="grid grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-white">Username *</Label>
                      <Input
                        required
                        value={formData.username}
                        onChange={(e) => setFormData({...formData, username: e.target.value})}
                        placeholder="Contoh: admincepu, herman, jafar (unik per tenant)"
                        className="h-10 dark:bg-slate-700 dark:text-white dark:border-slate-600"
                        data-testid="user-username-input"
                      />
                      <p className="text-[10px] text-gray-500 dark:text-slate-400">Gunakan huruf kecil tanpa spasi, tanpa domain @ (bukan email)</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-white">Full Name *</Label>
                      <Input
                        required
                        value={formData.full_name}
                        onChange={(e) => {
                          const nv = e.target.value;
                          setFormData(prev => {
                            const next = { ...prev, full_name: nv };
                            // 2026-02: Auto-suggest username kalau username masih kosong (hanya di mode Create)
                            if (!isEditMode && !prev.username) {
                              next.username = nv.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 30);
                            }
                            return next;
                          });
                        }}
                        placeholder="Contoh: Admin Cepu, Herman Teknisi"
                        className="h-10 dark:bg-slate-700 dark:text-white dark:border-slate-600"
                        data-testid="user-fullname-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-white">Email *</Label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="h-10 dark:bg-slate-700 dark:text-white dark:border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-white">Phone</Label>
                      <Input
                        type="tel"
                        placeholder="628123456789 (hanya angka)"
                        value={formData.phone}
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          setFormData({...formData, phone: formatted});
                          const error = getPhoneValidationError(formatted);
                          setPhoneError(error || '');
                        }}
                        className={`h-10 dark:bg-slate-700 dark:text-white dark:border-slate-600 ${phoneError ? 'border-red-500' : ''}`}
                      />
                      {phoneError && (
                        <p className="text-xs text-red-500">{phoneError}</p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-slate-400">Format: angka saja</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-white">Password {isEditMode && <span className="text-xs text-gray-500 dark:text-slate-400">(leave empty to keep current)</span>}</Label>
                      <Input
                        type="password"
                        required={!isEditMode}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className="h-10 dark:bg-slate-700 dark:text-white dark:border-slate-600"
                        placeholder={isEditMode ? 'Leave empty to keep current password' : ''}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-white">Role *</Label>
                      <select
                        className="w-full h-10 px-3 text-sm border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                      >
                        {roles.length > 0 ? (
                          roles
                            .filter(role => role.is_active !== false)
                            .map(role => (
                              <option key={role.name} value={role.name}>
                                {role.description || role.name}
                                {role.is_system_role ? '' : ' (Custom)'}
                              </option>
                            ))
                        ) : (
                          <>
                            <option value="admin">Administrator</option>
                            <option value="kepala_cabang">Kepala Cabang</option>
                            <option value="cs">Customer Service</option>
                            <option value="teknisi">Teknisi</option>
                          </>
                        )}
                      </select>
                      {roles.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                          💡 Custom roles dapat dibuat di menu Roles & Permissions
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label className="text-sm font-medium text-gray-700 dark:text-white">Branch *</Label>
                      <select
                        required
                        className="w-full h-10 px-3 text-sm border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.branch_id}
                        onChange={(e) => setFormData({...formData, branch_id: e.target.value})}
                      >
                        <option value="">Select Branch</option>
                        {branches.map(branch => (
                          <option key={branch.id} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-5 border-t border-gray-200 dark:border-slate-700">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="h-10 px-5">
                      Cancel
                    </Button>
                    <Button type="submit" className="h-10 bg-indigo-600 hover:bg-indigo-700 font-medium px-5">
                      {isEditMode ? 'Update User' : 'Create User'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-6">
        {/* Search Bar */}
        <Card className="mb-6 border-0 shadow-sm">
          <CardContent className="py-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                data-testid="search-user-input"
                placeholder="Search users by name, username, or email..."
                className="h-10 pl-10 bg-white border-gray-300 dark:bg-slate-800 dark:border-slate-600"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border-0 shadow-sm dark:bg-slate-800">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                  <tr>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">User</th>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">Role</th>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">Branch</th>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">Contact</th>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-6 text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => {
                    const branch = branches.find(b => String(b.id) === String(user.branch_id)) || (user.branch_name ? { name: user.branch_name } : null);
                    return (
                      <tr key={index} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {user.full_name?.charAt(0)?.toUpperCase() || user.username?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{user.full_name || 'N/A'}</div>
                              <div className="text-xs text-gray-500 dark:text-slate-400">@{user.username || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <Badge className={`text-xs ${getRoleBadgeColor(user.role)}`}>
                            <Shield className="w-3 h-3 mr-1" />
                            {getRoleLabel(user.role)}
                          </Badge>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-sm text-gray-600 dark:text-slate-300">
                            {branch ? branch.name : '-'}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="text-sm text-gray-600 dark:text-slate-300">{user.email}</div>
                          <div className="text-xs text-gray-500 dark:text-slate-400">{user.phone || '-'}</div>
                        </td>
                        <td className="py-4 px-6">
                          {user.is_active ? (
                            <Badge className="text-xs bg-emerald-500 hover:bg-emerald-600">
                              <UserCheck className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300">
                              <UserX className="w-3 h-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 w-8 p-0 text-indigo-600 hover:bg-indigo-50 border-indigo-200 dark:text-indigo-400 dark:hover:bg-slate-600"
                              onClick={() => handleEdit(user)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 border-red-200 dark:text-red-400 dark:hover:bg-slate-600"
                              onClick={() => handleDelete(user.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Users;
